import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { getUsageStats, trackUsage as apiTrackUsage } from '../utils/api';
import { getUsageLimit, getTimeUntilReset, USAGE_FEATURES } from '../constants/UsageLimits';

export const useUsageTracking = (feature) => {
  const { email, tier, tierLimits, globalTierLimits } = useUser();
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState({ count: 0, limit: 0 });
  const [lastReset, setLastReset] = useState(Date.now());
  const [error, setError] = useState(null);
  /** Bumps when assumption keys may have changed (focus / other tab / DevTools). */
  const [, bumpAssumptionRead] = useState(0);

  const getAssumedExceeded = useCallback(() => {
    try {
      if (typeof window === 'undefined' || !window?.localStorage) return false;
      const biqFlag = window.localStorage.getItem('bloom_assume_biq_exceeded');
      if (feature === USAGE_FEATURES.BIQ_QUERY && (biqFlag === 'true' || biqFlag === '1')) {
        return true;
      }
      const deepFlag = window.localStorage.getItem('bloom_assume_deep_thinking_exceeded');
      if (
        feature === USAGE_FEATURES.BIQ_DEEP_THINKING &&
        (deepFlag === 'true' || deepFlag === '1')
      ) {
        return true;
      }
      const raw = window.localStorage.getItem('bloom_assume_usage_exceeded');
      if (!raw) return false;
      const normalized = raw.trim().toLowerCase();
      if (!normalized) return false;
      if (normalized === 'true' || normalized === '*' || normalized === 'all') return true;
      return normalized === String(feature).trim().toLowerCase();
    } catch {
      return false;
    }
  }, [feature]);

  // Read assumptions every render so clearing localStorage takes effect after navigation or bump.
  const assumedExceeded = getAssumedExceeded();

  useEffect(() => {
    const bump = () => bumpAssumptionRead((n) => n + 1);
    const onStorage = (e) => {
      if (
        !e ||
        e.key === 'bloom_assume_usage_exceeded' ||
        e.key === 'bloom_assume_biq_exceeded' ||
        e.key === 'bloom_assume_deep_thinking_exceeded'
      ) {
        bump();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bump();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // `count >= limit` covers limit 0 (no allowance): any count >= 0 is "at or over" zero quota.
  const isExceeded =
    assumedExceeded ||
    (!loading &&
      !!email &&
      !!tier &&
      usage.limit !== Infinity &&
      usage.count >= usage.limit);
  // Local override should always win (even for "unlimited" tiers).
  const isUnlimited = usage.limit === Infinity && !assumedExceeded;
  const remaining = Math.max(0, usage.limit - usage.count);

  const fetchUsageStats = useCallback(async () => {
    if (!email || !tier) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getUsageStats(email);
      const fallbackLimit = getUsageLimit(tier, feature, tierLimits, globalTierLimits);

      if (response.status && response.usage) {
        const raw = response.usage[feature];
        const count = Math.max(0, Number(raw?.count ?? 0) || 0);
        let limit = raw?.limit;
        if (limit === undefined || limit === null) {
          limit = fallbackLimit;
        } else {
          const limitNum = Number(limit);
          if (limitNum === -1) {
            limit = Infinity;
          } else if (!Number.isFinite(limitNum) || limitNum === 0) {
            limit = fallbackLimit;
          } else {
            limit = limitNum;
          }
        }
        setUsage({ count, limit });
        setLastReset(response.last_reset || Date.now());
      } else {
        setUsage({ count: 0, limit: fallbackLimit });
        if (response?.last_reset) setLastReset(response.last_reset);
      }
    } catch (err) {
      console.error('Failed to fetch usage stats:', err);
      setError(err.message);

      const limit = getUsageLimit(tier, feature, tierLimits, globalTierLimits);
      setUsage({ count: 0, limit });
    } finally {
      setLoading(false);
    }
  }, [email, tier, feature, tierLimits, globalTierLimits]);

  const trackUsage = useCallback(async () => {
    if (!email || isExceeded || isUnlimited) return { success: false, exceeded: isExceeded };

    try {
      const response = await apiTrackUsage(email, feature, 1);

      if (response.status) {
        setUsage((prev) => {
          let nextLimit = response.limit;
          if (nextLimit !== undefined && nextLimit !== null) {
            const ln = Number(nextLimit);
            nextLimit = ln === -1 ? Infinity : ln;
            if (!Number.isFinite(nextLimit) && nextLimit !== Infinity) {
              nextLimit = prev.limit;
            }
          } else {
            nextLimit = prev.limit;
          }
          return {
            count: Math.max(0, Number(response.count ?? 0) || 0),
            limit: nextLimit,
          };
        });

        return {
          success: true,
          exceeded: response.exceeded || false,
          remaining: response.limit - response.count,
        };
      }

      return { success: false, exceeded: false };
    } catch (err) {
      console.error('Failed to track usage:', err);
      // Do not optimistically set exceeded on API error to avoid false "out of quota" messages
      return { success: false, exceeded: false };
    }
  }, [email, feature, isExceeded, isUnlimited, usage.count, usage.limit]);

  useEffect(() => {
    fetchUsageStats();
  }, [fetchUsageStats]);

  useEffect(() => {
    const { msUntilReset } = getTimeUntilReset(lastReset, tierLimits?.reset_period_days);

    if (msUntilReset > 0) {
      const timer = setTimeout(() => {
        fetchUsageStats();
      }, msUntilReset);

      return () => clearTimeout(timer);
    }
  }, [lastReset, fetchUsageStats]);

  return {
    loading,
    error,
    usage,
    isExceeded,
    isUnlimited,
    remaining,
    lastReset,
    trackUsage,
    refetch: fetchUsageStats,
  };
};
